import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseHttpService } from './base-http.service';
import {
  AuthValidationRequest,
  AuthValidationResponse,
  UserInfo
} from '../models';
import { clearCache } from '../interceptors/cache.interceptor-fn';

/**
 * Authentication Service for IH API
 * Handles phone-based authentication and user information
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseHttpService {
  private readonly AUTH_ENDPOINT = 'auth';

  /**
   * Validate phone number access
   * POST /api/ih/auth/validate
   *
   * Uses postRaw (not post): this endpoint returns the bare
   * IHAuthValidationResponseDto `{ isValid, message, userInfo }` — it does NOT
   * use the `{ status, message, data }` envelope that BaseHttpService.post
   * unwraps. Going through post() would treat the (absent) `status` as a
   * failure and throw on every successful call.
   */
  validatePhone(request: AuthValidationRequest): Observable<AuthValidationResponse> {
    return this.postRaw<AuthValidationResponse>(`${this.AUTH_ENDPOINT}/validate`, request);
  }

  /**
   * Get user information by phone number
   * GET /api/ih/auth/user-info/{phone}
   */
  getUserInfo(phone: string): Observable<UserInfo> {
    return this.get<UserInfo>(`${this.AUTH_ENDPOINT}/user-info/${phone}`);
  }

  /**
   * Check if phone has access to specific policy
   * GET /api/ih/auth/check-access/{phone}/{policyNo}
   */
  checkPolicyAccess(phone: string, policyNo: string): Observable<boolean> {
    return this.getRaw<boolean>(`${this.AUTH_ENDPOINT}/check-access/${phone}/${policyNo}`);
  }

  /**
   * Store user phone in session storage
   */
  setUserPhone(phone: string): void {
    sessionStorage.setItem('ih_user_phone', phone);
    // Now that an identity is known, record the app-open (deduped per session).
    this.recordAppOpen();
  }

  /**
   * Records one ihub app-open as a LOGIN in audit_logs, at most once per browser
   * session. Fire-and-forget — does not block the UI or surface errors to the
   * customer. Called from both the APP_INITIALIZER (deep-link / direct entry, cookie
   * already present) and setUserPhone() (normal home entry, phone arrives async via
   * the webview). The session flag guarantees validatePhone fires at most once,
   * whichever path resolves the phone first.
   */
  recordAppOpen(): void {
    if (sessionStorage.getItem('ih_login_logged')) { return; }   // already logged this session
    const phone = this.getUserPhone();                           // sessionStorage -> ih_phone cookie
    // Backend /api/ih/auth/validate requires exactly 8 digits; anything else would
    // 400 and uselessly churn the retry flag, so skip until a valid identity exists.
    if (!phone || !/^\d{8}$/.test(phone)) { return; }
    sessionStorage.setItem('ih_login_logged', '1');             // set BEFORE the call to avoid double-fire
    this.validatePhone({ phone }).subscribe({
      next: () => { /* logged */ },
      error: () => { sessionStorage.removeItem('ih_login_logged'); } // allow a later retry if it failed
    });
  }

  /**
   * Get user phone. Prefer sessionStorage (set by the home page when it
   * processes the host webview message), but fall back to the `ih_phone`
   * cookie the host webview sets directly. Without this fallback, any page
   * opened by deep-link / refresh / direct super-app navigation (i.e. NOT via
   * the home page first) has an empty sessionStorage and fails to load data
   * (e.g. claim/change/cancel lists showed "Phone number not available").
   */
  getUserPhone(): string | null {
    const fromSession = sessionStorage.getItem('ih_user_phone');
    if (fromSession) {
      return fromSession;
    }
    const match = document.cookie.match(/(?:^|;\s*)ih_phone=([^;]+)/);
    const fromCookie = match ? decodeURIComponent(match[1]) : null;
    if (fromCookie) {
      // cache so subsequent reads (and setUserPhone consumers) are consistent
      sessionStorage.setItem('ih_user_phone', fromCookie);
    }
    return fromCookie;
  }

  /**
   * Clear user session and invalidate cache
   */
  clearSession(): void {
    sessionStorage.removeItem('ih_user_phone');
    sessionStorage.removeItem('ih_user_info');
    
    // Clear HTTP cache to prevent data leakage between sessions
    clearCache();
  }

  /**
   * Store user info in session storage
   */
  setUserInfo(userInfo: UserInfo): void {
    sessionStorage.setItem('ih_user_info', JSON.stringify(userInfo));
  }

  /**
   * Get user info from session storage
   */
  getCachedUserInfo(): UserInfo | null {
    const userInfo = sessionStorage.getItem('ih_user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  }
}
